// Arbeid og produkter på et tekstiltrykk (ServiceDetailsClub, vinnsel 30.10): bare linjene som er
// lagt til vises, og «Legg til arbeid eller produkt» åpner en dialog: velg arbeid eller produkt, så
// skjemaet (arbeid fra prislisten eller eget, produkt fra /produkter eller eget). «Endre» åpner samme
// skjema. Hver endring lagres med en gang (onLagre), så pristilbudet følger med.
// Betalingen («Tøyet er betalt» = produktene, «Trykket er betalt» = arbeidet) styrer «Å betale» her
// og på arbeidsseddelen (src/trykk/linjer.js).
// Dialog etter U2 (useDialog): fokus inn, Tab holdes inne, Esc lukker, fokus tilbake.
import React, { useEffect, useRef, useState } from 'react';
import { useDialog } from '../components/tilgjengelighet';
import ProduktSok from '../components/ProduktSok';
import { Tekstfelt, Valgfelt, knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { Tall } from '../tilbud/VareLinjer';
import { linjeTall, medRabatt, regnPaNytt, kr } from '../tilbud/kalkyle';
import { linjeType, linjeBetalt, betalingssum } from './linjer';

const TOM = { artikkel: '', pris: '', antall: 1, rabatt: 0, rabattKr: 0 };
const prosentTekst = (n) => `${String(Math.round(n * 10) / 10).replace('.', ',')} %`;

function LinjeDialog({ apen, onLukk, start, arbeidsliste, onLagre }) {
  const dialogRef = useDialog(apen, onLukk);
  const tittelRef = useRef(null);
  const [steg, setSteg] = useState('velg'); // velg | produkt | skjema
  const [utkast, setUtkast] = useState(TOM);
  const [valgtArbeid, setValgtArbeid] = useState('');
  const [feil, setFeil] = useState('');

  useEffect(() => {
    if (!apen) return;
    setFeil('');
    setValgtArbeid('');
    if (start) { setUtkast({ ...start.linje, type: linjeType(start.linje) }); setSteg('skjema'); } else { setUtkast(TOM); setSteg('velg'); }
  }, [apen, start]);

  // Fokus til overskriften når dialogen bytter steg, så skjermlesere og tastatur følger med.
  const forsteSteg = useRef(true);
  useEffect(() => {
    if (!apen) { forsteSteg.current = true; return; }
    if (forsteSteg.current) { forsteSteg.current = false; return; }
    tittelRef.current?.focus();
  }, [steg, apen]);

  if (!apen) return null;

  const ny = !start;
  const erArbeid = utkast.type === 'arbeid';
  const endre = (felt, verdi) => setUtkast((u) => {
    if (felt === 'rabatt') return medRabatt(u, 'prosent', verdi);
    if (felt === 'rabattKr') return medRabatt(u, 'kr', verdi);
    if (felt === 'pris' || felt === 'antall') return regnPaNytt({ ...u, [felt]: verdi });
    return { ...u, [felt]: verdi };
  });
  const velgArbeid = (verdi) => {
    setValgtArbeid(verdi);
    const a = arbeidsliste.find((x) => String(x._id) === String(verdi));
    if (a) setUtkast((u) => regnPaNytt({ ...u, artikkel: a.title, pris: Number(a.price) || 0 }));
  };
  const fraProduktliste = ({ navn, variant, storrelse, produktId }) => {
    setUtkast(regnPaNytt({
      ...TOM,
      type: 'produkt',
      artikkel: [navn, storrelse && `str. ${storrelse}`].filter(Boolean).join(', '),
      produktId: produktId || '',
      variantId: variant.id || '',
      ean: variant.ean || '',
      pris: variant.pris ?? '',
      kostpris: variant.kostpris ?? '',
    }));
    setSteg('skjema');
  };
  const lagre = (e) => {
    e.preventDefault();
    if (!String(utkast.artikkel).trim()) { setFeil(erArbeid ? 'Skriv hva arbeidet er.' : 'Skriv navnet på produktet.'); return; }
    if (utkast.pris === '' || Number.isNaN(Number(String(utkast.pris).replace(',', '.')))) { setFeil('Skriv en pris.'); return; }
    onLagre({ ...utkast, artikkel: String(utkast.artikkel).trim() });
  };

  const tittel = steg === 'velg' ? 'Legg til arbeid eller produkt'
    : steg === 'produkt' ? 'Legg til produkt'
      : `${ny ? 'Legg til' : 'Endre'} ${erArbeid ? 'arbeid' : 'produkt'}`;
  const t = linjeTall(utkast);

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-2 sm:p-5">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="tl-tittel" className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-2 border-b border-slate-200 px-3 py-1.5 sm:px-3">
          <h2 id="tl-tittel" ref={tittelRef} tabIndex={-1} className="min-w-0 flex-1 text-base font-bold text-slate-900 focus:outline-none">{tittel}</h2>
          <button type="button" onClick={onLukk} className={`${knappSekundar} shrink-0`}>Avbryt</button>
        </div>

        <div className="space-y-3 px-3 py-3 sm:px-3">
          {steg === 'velg' && (
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" data-autofokus onClick={() => { setUtkast({ ...TOM, type: 'arbeid' }); setSteg('skjema'); }}
                className="flex min-h-[5rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-slate-300 bg-white p-3 text-base font-bold text-slate-900 hover:border-indigo-600 hover:bg-indigo-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
                <Ikon navn="genser" className="w-6 h-6" /> Arbeid
                <span className="text-sm font-normal text-slate-700">Trykk fra prislisten eller eget arbeid</span>
              </button>
              <button type="button" onClick={() => setSteg('produkt')}
                className="flex min-h-[5rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-slate-300 bg-white p-3 text-base font-bold text-slate-900 hover:border-indigo-600 hover:bg-indigo-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
                <Ikon navn="lapp" className="w-6 h-6" /> Produkt
                <span className="text-sm font-normal text-slate-700">Tøy og varer fra produktlisten</span>
              </button>
            </div>
          )}

          {steg === 'produkt' && (
            <>
              <ProduktSok id="tl-produkt" onVelg={fraProduktliste} visLager />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setSteg('velg')} className={knappSekundar}>Tilbake</button>
                <button type="button" onClick={() => { setUtkast({ ...TOM, type: 'produkt' }); setSteg('skjema'); }} className={knappSekundar}>
                  Varen finnes ikke i produktlisten
                </button>
              </div>
            </>
          )}

          {steg === 'skjema' && (
            <form onSubmit={lagre} className="space-y-3" noValidate>
              {erArbeid && ny && arbeidsliste.length > 0 && (
                <Valgfelt id="tl-arbeidsliste" etikett="Arbeid fra prislisten" value={valgtArbeid} onChange={(e) => velgArbeid(e.target.value)}>
                  <option value="">Velg, eller skriv eget arbeid under</option>
                  {arbeidsliste.map((a) => <option key={a._id} value={a._id}>{a.title} – {kr(a.price)}</option>)}
                </Valgfelt>
              )}
              <Tekstfelt id="tl-artikkel" etikett={erArbeid ? 'Arbeid' : 'Produkt'} value={utkast.artikkel} onChange={(e) => endre('artikkel', e.target.value)} />
              <div className="flex flex-wrap gap-2">
                <Tall id="tl-pris" etikett="Pris inkl. mva" enhet="kr" value={utkast.pris} onChange={(v) => endre('pris', v)} />
                <Tall id="tl-antall" etikett="Antall" value={utkast.antall} onChange={(v) => endre('antall', v)} bredde="w-20" />
                <Tall id="tl-rabatt" etikett="Rabatt i %" enhet="%" value={utkast.rabatt} onChange={(v) => endre('rabatt', v)} bredde="w-24" />
                <Tall id="tl-rabattkr" etikett="Rabatt i kr" enhet="kr" value={utkast.rabattKr} onChange={(v) => endre('rabattKr', v)} />
              </div>
              <p className="text-slate-900">Linjesum: <span className="text-lg font-bold">{kr(t.total)}</span></p>
              {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
              <div className="flex flex-wrap gap-2">
                <button type="submit" className={knappPrimar}><Ikon navn="hake" className="w-6 h-6" /> {ny ? 'Legg til' : 'Lagre'}</button>
                {ny && <button type="button" onClick={() => setSteg(erArbeid ? 'velg' : 'produkt')} className={knappSekundar}>Tilbake</button>}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// linjer: lagrede linjer; onLagre(nyListe) lagrer og gir true/false. betaling: { toyBetalt, trykkBetalt };
// onBetaling(felt, verdi) lagrer betalingen.
export default function TrykkLinjer({ linjer, arbeidsliste, onLagre, lagrer, betaling, onBetaling, melding, feil }) {
  const [dialog, setDialog] = useState(null); // null | { ny: true } | { indeks, linje }
  const sum = betalingssum(linjer, betaling);
  const rabatt = linjer.reduce((s, l) => s + linjeTall(l).rabattKr, 0);

  const lagreLinje = async (linje) => {
    const nye = dialog?.linje ? linjer.map((l, i) => (i === dialog.indeks ? linje : l)) : [...linjer, linje];
    if (await onLagre(nye)) setDialog(null);
  };

  return (
    <section aria-labelledby="tr-linjer" className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 sm:p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 basis-60">
          <h2 id="tr-linjer" className="text-base font-bold text-slate-900">Arbeid og produkter</h2>
          <p className="text-sm text-slate-600">Dette blir arbeidsseddelen og pristilbudet.</p>
        </div>
        <button type="button" onClick={() => setDialog({ ny: true })} disabled={lagrer} className={knappPrimar}>
          <Ikon navn="pluss" className="w-6 h-6" /> Legg til arbeid eller produkt
        </button>
      </div>

      {linjer.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-slate-700">Ingen arbeid eller produkter ennå.</p>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200">
          {linjer.map((l, i) => {
            const t = linjeTall(l);
            const betalt = linjeBetalt(l, betaling);
            return (
              <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-1.5">
                <div className="min-w-0 flex-1 basis-56">
                  <p className="font-semibold text-slate-900 [overflow-wrap:anywhere]">
                    <span className="mr-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{linjeType(l) === 'arbeid' ? 'Arbeid' : 'Produkt'}</span>
                    {l.artikkel}
                  </p>
                  <p className="text-sm text-slate-700">
                    {Math.max(1, Math.round(Number(l.antall) || 1))} × {kr(l.pris)}
                    {t.rabattKr > 0 && ` · rabatt ${kr(t.rabattKr)} (${prosentTekst((t.rabattKr / (t.subtotal || 1)) * 100)})`}
                  </p>
                </div>
                <p className="shrink-0 text-right">
                  <span className={`block text-base font-bold ${betalt ? 'text-slate-600 line-through' : 'text-slate-900'}`}>{kr(t.total)}</span>
                  {betalt && <span className="block text-sm font-semibold text-green-800">Betalt</span>}
                </p>
                <div className="flex shrink-0 gap-1">
                  <button type="button" onClick={() => setDialog({ indeks: i, linje: l })} disabled={lagrer}
                    className="inline-flex min-h-[2.25rem] items-center rounded-lg px-3 font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
                    Endre<span className="sr-only"> {l.artikkel}</span>
                  </button>
                  <button type="button" onClick={() => onLagre(linjer.filter((_, j) => j !== i))} disabled={lagrer}
                    className="inline-flex min-h-[2.25rem] items-center rounded-lg px-3 font-semibold text-red-800 hover:bg-red-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
                    Fjern<span className="sr-only"> {l.artikkel}</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <fieldset className="min-w-0 flex flex-wrap gap-x-4 gap-y-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5">
        <legend className="px-1 font-semibold text-slate-900">Betaling</legend>
        <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 text-slate-800">
          <input type="checkbox" checked={!!betaling.toyBetalt} disabled={lagrer} onChange={(e) => onBetaling('toyBetalt', e.target.checked)} className="h-5 w-5" />
          Tøyet er betalt (produktene)
        </label>
        <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 text-slate-800">
          <input type="checkbox" checked={!!betaling.trykkBetalt} disabled={lagrer} onChange={(e) => onBetaling('trykkBetalt', e.target.checked)} className="h-5 w-5" />
          Trykket er betalt (arbeidet)
        </label>
      </fieldset>

      <dl className="space-y-1 border-t-2 border-slate-300 pt-3 text-slate-900">
        <div className="flex justify-between gap-2"><dt>Totalt{rabatt > 0 ? ` (rabatt ${kr(rabatt)})` : ''}</dt><dd>{kr(sum.total)}</dd></div>
        {sum.betalt > 0 && <div className="flex justify-between gap-2"><dt>Betalt</dt><dd>− {kr(sum.betalt)}</dd></div>}
        <div className="flex justify-between gap-2 text-lg font-bold"><dt>Å betale</dt><dd>{kr(sum.aBetale)}</dd></div>
      </dl>
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <p role="status" className="text-sm text-slate-700">{lagrer ? 'Lagrer …' : melding}</p>

      <LinjeDialog
        apen={!!dialog}
        onLukk={() => setDialog(null)}
        start={dialog?.linje ? dialog : null}
        arbeidsliste={arbeidsliste}
        onLagre={lagreLinje}
      />
    </section>
  );
}
